import React, { useLayoutEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Pencil, XCircle, Loader2, CheckCircle2, ClockIcon } from "lucide-react";
import { RootState, useAppDispatch } from "../../../redux/store";
import {
    getTimeoffRequestDetails,
    cancelTimeOffRequest,
    setTimeoffToUpdate,
    setTimeoffUpdateMode,
} from "../../../redux/features/timeoff";
import { setShowTopNav } from "../../../redux/features/mobileLayout";
import { getApprovalStatusCheck, getApprovalStatusText } from "../../../utils/timeoff";
import { DateTimeUtil } from "../../../utils";
import { useNavigationHistory } from "../../../hooks/useNavigationHistory";
import { setGlobalBackHandler, clearGlobalBackHandler } from "../../../hooks";
import {
    TimeoffRequest,
    TimeoffWorkflowDetails,
    TimeoffWorkflowActor,
    TimeOffRequestDetails,
} from "../../../types/model/timeoff";

const TimeOffView: React.FC = () => {
    // Redux hooks and navigation
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { setSourcePath } = useNavigationHistory();
    const locationState = (location.state || {}) as {
        from?: string;
        fromState?: Record<string, unknown>;
        readOnly?: boolean;
    };
    const backPath = locationState.from || "/m-timeoff";

    // Selectors for timeoff and user state
    const { selectedTimeoffForView } = useSelector((state: RootState) => state.timeoff);
    const { timeoffDetails, isLoadingTimeoffDetails } = useSelector((state: RootState) => state.timeoff) as {
        timeoffDetails?: TimeoffRequest;
        isLoadingTimeoffDetails: boolean;
    };

    // Fetch timeoff details on mount or when selectedTimeoffForView changes
    useLayoutEffect(() => {
        if (selectedTimeoffForView?.time_off_request_id) {
            dispatch(getTimeoffRequestDetails({ time_off_request_id: selectedTimeoffForView.time_off_request_id }));
        }

        // Set up navigation history
        setSourcePath(backPath);

        // Set up global back handler to navigate back to the source timeoff screen
        setGlobalBackHandler(() => {
            if (locationState.fromState) {
                navigate(backPath, { state: locationState.fromState });
            } else {
                navigate(backPath);
            }
            return true;
        });

        // Clean up when component unmounts
        return () => {
            clearGlobalBackHandler();
        };
    }, [selectedTimeoffForView, navigate, setSourcePath, dispatch, backPath, locationState.fromState]);

    // Show loading spinner while fetching details
    if (isLoadingTimeoffDetails) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    // Handlers for edit and cancel actions
    const handleEdit = () => {
        dispatch(setTimeoffUpdateMode(true));
        dispatch(setTimeoffToUpdate(timeoffDetails as any));
        navigate("/m-request-timeoff");
    };

    const handleCancel = async () => {
        await dispatch(cancelTimeOffRequest({ request_id: timeoffDetails?.time_off_request_id! }));
        dispatch(setShowTopNav(true));
        navigate("/mobile-dashboard");
    };

    // Guard for missing details
    if (!timeoffDetails) return null;

    const getFormattedTimeRange = (day: TimeOffRequestDetails): string | null => {
        if (!day?.start_time || !day?.end_time) {
            return null;
        }

        const resolveBaseDate = (): string | null => {
            if (!day.date) return null;

            if (typeof day.date === "string") {
                const [datePart] = day.date.split(" ");
                return datePart ?? null;
            }

            const parsedDate = new Date(day.date as unknown as string | number | Date);

            if (Number.isNaN(parsedDate.getTime())) {
                return null;
            }

            return DateTimeUtil.formatDateToMySQL(parsedDate, true);
        };

        const baseDate = resolveBaseDate();

        if (!baseDate) return null;

        const range = DateTimeUtil.formatElegantTimeRange(
            `${baseDate} ${day.start_time}`,
            `${baseDate} ${day.end_time}`
        );

        return range || null;
    };

    return (
        <div className="w-full max-w-md mx-auto bg-white shadow-md rounded-lg p-4 min-h-screen">
            {/* Header and note */}
            <div className="mb-4">
                <h2 className="text-xl font-semibold text-blue-600">Time Off Request</h2>
                <p className="mt-1 text-gray-600 text-sm italic">Note: {timeoffDetails.notes || "No note provided"}</p>
            </div>

            {/* Summary grid */}
            <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg shadow-sm text-sm text-gray-700">
                {/* Dates */}
                <div className="flex flex-col">
                    <span className="text-xs text-gray-500">Dates</span>
                    <span className="text-gray-900 font-medium">
                        {DateTimeUtil.formatDateRangeFancy(timeoffDetails.start_date, timeoffDetails.end_date, true)}
                    </span>
                </div>
                {/* Category */}
                <div className="flex flex-col">
                    <span className="text-xs text-gray-500">Category</span>
                    <span className="text-gray-900 font-medium">{timeoffDetails.category?.category_name}</span>
                </div>
                {/* Amount */}
                <div className="flex flex-col">
                    <span className="text-xs text-gray-500">Amount</span>
                    <span className="text-gray-900 font-medium">
                        {timeoffDetails.amount_requested} {timeoffDetails.category?.track_time_in}
                    </span>
                </div>
                {/* Status */}
                <div className="flex flex-col">
                    <span className="text-xs text-gray-500">Status</span>
                    <span
                        className={`inline-flex items-center space-x-1 font-medium ${getApprovalStatusText(
                            timeoffDetails.status
                        )}`}
                    >
                        {getApprovalStatusCheck(timeoffDetails.status)}
                        <span>{timeoffDetails.status}</span>
                    </span>
                </div>
            </div>

            {/* Approval History Section */}
            <div className="my-6">
                <h3 className="text-md font-semibold text-blue-600 mb-2">Approval History</h3>
                <div className="bg-gray-50 rounded-lg p-4 border">
                    {timeoffDetails.workflow_details && timeoffDetails.workflow_details.length > 0 ? (
                        <div className="flex flex-col space-y-0 relative">
                            {timeoffDetails.workflow_details.map((step: TimeoffWorkflowDetails, idx: number) => {
                                const actors: TimeoffWorkflowActor[] = Object.values(step.actor_json || {});
                                const actorNames = actors.map((actor) => actor.name).join(", ");
                                // Collect all acted results (approved/rejected) for this step
                                const acted: {
                                    actor: TimeoffWorkflowActor;
                                    action: string;
                                    date: string | undefined;
                                    note: string | undefined;
                                    status: string | undefined;
                                }[] = [];
                                if (step.wf_task_acted_json?.RESULT) {
                                    (step.wf_task_acted_json.RESULT.APPROVED || []).forEach((actor, i) => {
                                        acted.push({
                                            actor,
                                            action: "Approved",
                                            date: step.wf_task_acted_json?.APPROVED_ON,
                                            note: step.wf_task_acted_json?.APPROVAL_NOTE,
                                            status: "APPROVED",
                                        });
                                    });
                                    (step.wf_task_acted_json.RESULT.REJECTED || []).forEach((actor, i) => {
                                        acted.push({
                                            actor,
                                            action: "Rejected",
                                            date: step.wf_task_acted_json?.APPROVED_ON, // Use APPROVED_ON for both
                                            note: step.wf_task_acted_json?.APPROVAL_NOTE,
                                            status: "REJECTED",
                                        });
                                    });
                                }
                                const isPending = !acted.length && (step.current_status === "PENDING" || step.current_status === "0");
                                const isRejected = acted.some((a) => a.status === "REJECTED" || step.current_status === "0") || (step.current_status === "REJECTED" || step.current_status === "-1");
                                return (
                                    <div key={idx} className="flex items-stretch relative min-h-[80px]">
                                        {/* Draw vertical line to connect steps visually, except for last step */}
                                        {idx !== timeoffDetails.workflow_details.length - 1 && (
                                            <div className="absolute left-4 top-8 w-px h-[calc(100%_-_16px)] bg-gray-200 z-0"></div>
                                        )}
                                        {/* Avatars: show up to 2 side by side, smaller size */}
                                        <div className="flex flex-col items-center mr-4 z-10">
                                            <div className="flex -space-x-2">
                                                {actors.slice(0, 2).map((actor, i) => (
                                                    actor.image_url ? (
                                                        <img
                                                            key={i}
                                                            src={actor.image_url}
                                                            alt={actor.name}
                                                            className="w-8 h-8 rounded-full object-cover border border-gray-200 shadow-sm bg-gray-100"
                                                        />
                                                    ) : (
                                                        <div
                                                            key={i}
                                                            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 border border-gray-200 shadow-sm"
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5.121 17.804A9 9 0 1112 21a8.963 8.963 0 01-6.879-3.196z" />
                                                            </svg>
                                                        </div>
                                                    )
                                                ))}
                                            </div>
                                        </div>
                                        {/* Step details: actor names, actions, status, notes */}
                                        <div className="flex-1 flex flex-col justify-center">
                                            <span className="font-semibold text-gray-900 text-base leading-tight">
                                                {actorNames || "-"}
                                            </span>
                                            {/* Pending state */}
                                            {isPending && (
                                                <div className="mt-1 mb-3 flex items-center space-x-2">
                                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold text-gray-500 bg-gray-100 mr-2">
                                                        <ClockIcon className="w-4 h-4 mr-1 text-gray-400" />
                                                        Waiting
                                                    </span>
                                                </div>
                                            )}
                                            {/* Acted (approved/rejected) */}
                                            {acted.length > 0 && acted.map((item, i) => (
                                                <div key={i} className="mt-1 mb-3">
                                                    <div className="flex items-center">
                                                        {/* Status badge with actor name and date in sentence format */}
                                                        {item.status === "APPROVED" && (
                                                            <span className="inline-flex items-center text-sm">
                                                                <CheckCircle2 className="w-4 h-4 mr-1 text-green-600" />
                                                                <span className="text-gray-800">
                                                                    Approved by <span className="font-medium">{item.actor.name}</span>
                                                                    {item.date && (
                                                                        <span className="text-gray-600"> on {DateTimeUtil.formatDateRangeFancy(item.date, item.date, true)}</span>
                                                                    )}
                                                                </span>
                                                            </span>
                                                        )}
                                                        {item.status === "REJECTED" && (
                                                            <span className="inline-flex items-center text-sm">
                                                                <XCircle className="w-4 h-4 mr-1 text-red-600" />
                                                                <span className="text-gray-800">
                                                                    Rejected by <span className="font-medium">{item.actor.name}</span>
                                                                    {item.date && (
                                                                        <span className="text-gray-600"> on {DateTimeUtil.formatDateRangeFancy(item.date, item.date, true)}</span>
                                                                    )}
                                                                </span>
                                                            </span>
                                                        )}
                                                    </div>
                                                    {/* Show approval/rejection note if present */}
                                                    {item.note && (
                                                        <span className="block mt-1 text-gray-500 text-sm italic pl-5">
                                                            Note: {item.note}
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                            {/* Fallback: show current status and any notes if no one has acted yet and not pending */}
                                            {!isPending && acted.length === 0 && (
                                                <div className="mt-1 mb-3">
                                                    {step.status_text && (
                                                        <span className="text-gray-500 text-xs block">
                                                            {step.status_text}
                                                        </span>
                                                    )}
                                                    {isRejected && (
                                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold text-red-600 bg-red-50 mr-2">
                                                            <XCircle className="w-4 h-4 mr-1" />
                                                            Rejected
                                                        </span>
                                                    )}
                                                    {step.current_status && !isRejected && (
                                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold text-gray-500 bg-gray-100 mr-2">
                                                            <ClockIcon className="w-4 h-4 mr-1 text-gray-400" />
                                                            Waiting
                                                        </span>
                                                    )}
                                                    {step.wf_task_acted_json?.APPROVAL_NOTE && (
                                                        <span className="block mt-1 text-gray-500 text-xs italic">
                                                            {step.wf_task_acted_json.APPROVAL_NOTE}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : null}
                </div>
            </div>

            {/* Breakdown Table */}
            <div className="mt-4 h-48 overflow-y-auto">
                <h3 className="text-md font-semibold text-blue-600 mb-2">Breakdown</h3>
                <table className="w-full text-sm text-gray-600">
                    <thead className="border-b bg-gray-100">
                        <tr>
                            <th className="py-2 px-3 text-left">Date</th>
                            <th className="py-2 px-3 text-left">{timeoffDetails.category?.track_time_in}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {timeoffDetails.details_json?.days.map((day, i) => {
                            const timeRange = getFormattedTimeRange(day as TimeOffRequestDetails);
                            return (
                                <tr key={i} className="border-b">
                                    <td className="py-2 px-3">{DateTimeUtil.formatShortDate(day.date)}</td>
                                    <td className="py-2 px-3">
                                        <div className="flex flex-col">
                                            <span>{day.hours ?? 0}</span>
                                            {timeRange && (
                                                <span className="text-xs text-gray-500">{timeRange}</span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Edit/Cancel Buttons for Pending Requests */}
            {timeoffDetails.status === "PENDING" && !locationState.readOnly && (
                <div className="flex space-x-4 mt-4">
                    <button
                        onClick={handleEdit}
                        className="flex items-center bg-blue-500 text-white px-4 py-2 rounded-md shadow-md hover:bg-blue-600 transition"
                    >
                        <Pencil className="w-5 h-5 mr-2" />
                        Edit
                    </button>
                    <button
                        onClick={handleCancel}
                        className="flex items-center bg-red-500 text-white px-4 py-2 rounded-md shadow-md hover:bg-red-600 transition"
                    >
                        <XCircle className="w-5 h-5 mr-2" />
                        Cancel
                    </button>
                </div>
            )}
        </div>
    );
};

export default TimeOffView;
